// src/pages/SessionsPage.js - Modern Dashboard Style
import React, { useState, useEffect } from 'react';
import { auth } from '../services/firebaseConfig';
import { useNavigate } from 'react-router-dom';
import '../styles/SessionsPage.css';
import NavBar from '../components/NavBar';
import Footer from '../components/Footer';
import { config } from '../config/config';

const SessionsPage = () => {
  const [newSession, setNewSession] = useState('');
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const [activeSessions, setActiveSessions] = useState([]);
  const [inactiveSessions, setInactiveSessions] = useState([]);
  const [sessionEligibility, setSessionEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'history'

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (currentUser) => {
      setUser(currentUser);

      if (currentUser) {
        try {
          const token = await currentUser.getIdToken();
          
          // Fetch session eligibility first
          const eligibilityResponse = await fetch(`${config.apiUrl}/account/session-eligibility`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          const eligibilityData = await eligibilityResponse.json();
          setSessionEligibility(eligibilityData);
          
          // Fetch sessions list
          const response = await fetch(`${config.apiUrl}/sessions`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          const data = await response.json();
          
          // Make sure we're accessing the correct property of the response
          const allSessions = data.sessions || data || [];
          
          console.log('All sessions from backend:', allSessions);
          
          // Active sessions: Only ACTIVE (currently playing)
          const active = allSessions.filter(session => 
            session.status === 'ACTIVE'
          );
          
          // Inactive/History sessions: INACTIVE, EXPIRED, COMPLETED, and DELETED
          const inactive = allSessions.filter(session => 
            session.status === 'INACTIVE' ||
            session.status === 'EXPIRED' || 
            session.status === 'COMPLETED' || 
            session.status === 'DELETED'
          );
          
          console.log('Active sessions:', active);
          console.log('Inactive/History sessions:', inactive);
          
          setActiveSessions(active);
          setInactiveSessions(inactive);
        } catch (error) {
          console.error('Error fetching sessions:', error);
        } finally {
          setLoading(false);
        }
      } else {
        setActiveSessions([]);
        setInactiveSessions([]);
        setSessionEligibility(null);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const handleCreateSession = async () => {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${config.apiUrl}/sessions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionName: newSession }),
      });
      
      const sessionData = await response.json();
      
      // Check whether the user needs to upgrade or has a conflicting active session
      if (sessionData.requiresUpgrade) {
        if (window.confirm(sessionData.message + '\n\nWould you like to upgrade now?')) {
          navigate('/pricing');
        }
        return;
      }

      // Check if there's an existing active session
      if (sessionData.existingSessionId) {
        if (window.confirm(sessionData.message + '\n\nWould you like to go to that session?')) {
          navigate(`/play-session/${sessionData.existingSessionId}`);
        }
        return;
      }

      // Check for error
      if (sessionData.error) {
        alert(sessionData.message || sessionData.error);
        return;
      }
      
      // Add the new session to active sessions
      setActiveSessions(prev => [...prev, {
        id: sessionData.sessionId,
        name: newSession,
        status: 'active'
      }]);
      
      navigate(`/play-session/${sessionData.sessionId}`);
      setNewSession('');
    } catch (error) {
      console.error('Error creating session:', error);
      alert('Failed to create session. Please try again.');
    }
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${config.apiUrl}/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        // Remove the session from both active and inactive lists
        setActiveSessions(prev => prev.filter(session => session.id !== sessionId));
        setInactiveSessions(prev => prev.filter(session => session.id !== sessionId));
      } else {
        console.error('Failed to delete session');
      }
    } catch (error) {
      console.error('Error deleting session:', error);
    }
  };

  const renderSkeletonCard = (key) => (
    <div key={key} className="skeleton-card">
      <div className="sk-header">
        <div className="skeleton-block sk-badge"></div>
        <div className="skeleton-block sk-badge-sm"></div>
      </div>
      <div className="card-body">
        <div className="skeleton-block sk-title"></div>
        <div className="sk-meta">
          <div className="skeleton-block sk-line sk-line-mid"></div>
          <div className="skeleton-block sk-line sk-line-short"></div>
        </div>
      </div>
      <div className="sk-actions">
        <div className="skeleton-block sk-btn"></div>
        <div className="skeleton-block sk-btn-icon"></div>
      </div>
    </div>
  );

  const renderSessionCard = (session, isActive = true) => {
    // Format dates - handle both Firestore Timestamp and regular Date objects
    let createdDate = null;
    if (session.createdDateTime) {
      if (session.createdDateTime.seconds || session.createdDateTime._seconds) {
        createdDate = new Date((session.createdDateTime.seconds || session.createdDateTime._seconds) * 1000);
      } else if (session.createdDateTime.toDate) {
        createdDate = session.createdDateTime.toDate();
      } else {
        createdDate = new Date(session.createdDateTime);
      }
      if (isNaN(createdDate.getTime())) {
        createdDate = null;
      }
    }
    
    let expiryDate = null;
    if (session.expiredDateTime) {
      if (session.expiredDateTime.seconds || session.expiredDateTime._seconds) {
        expiryDate = new Date((session.expiredDateTime.seconds || session.expiredDateTime._seconds) * 1000);
      } else if (session.expiredDateTime.toDate) {
        expiryDate = session.expiredDateTime.toDate();
      } else {
        expiryDate = new Date(session.expiredDateTime);
      }
      if (isNaN(expiryDate.getTime())) {
        expiryDate = null;
      }
    }
    
    // Calculate time left for active sessions
    const now = new Date();
    const timeLeft = expiryDate ? Math.max(0, expiryDate - now) : null;
    const minutesLeft = timeLeft ? Math.floor(timeLeft / 60000) : null;
    const hoursLeft = timeLeft ? Math.floor(timeLeft / 3600000) : null;

    return (
      <div key={session.id} className={`session-card ${isActive ? 'active' : 'history'}`}>
        <div className="card-header">
          <span className={`status-badge ${session.status.toLowerCase()}`}>
            <i className="fas fa-circle"></i>
            {session.status === 'ACTIVE' ? 'Live' : session.status}
          </span>
          {session.subscriptionPlan && (
            <span className={`plan-badge ${session.subscriptionPlan === 'free' ? 'free' : 'premium'}`}>
              <i className={`fas fa-${session.subscriptionPlan === 'free' ? 'gift' : 'crown'}`}></i>
              {session.subscriptionPlan === 'free' ? 'Free' : 'Premium'}
            </span>
          )}
        </div>
        
        <div className="card-body">
          <h3 className="session-title">{session.name || session.sessionName || 'Unnamed Session'}</h3>
          
          <div className="session-meta">
            {createdDate && (
              <div className="meta-item">
                <i className="fas fa-calendar-alt"></i>
                <span>{createdDate.toLocaleDateString()}</span>
              </div>
            )}
            {isActive && expiryDate && timeLeft > 0 && (
              <div className={`meta-item timer ${minutesLeft <= 10 ? 'warning' : ''}`}>
                <i className="fas fa-hourglass-half"></i>
                <span>
                  {hoursLeft > 0 
                    ? `${hoursLeft}h ${minutesLeft % 60}m left`
                    : `${minutesLeft}m left`
                  }
                </span>
              </div>
            )}
            {!isActive && expiryDate && (
              <div className="meta-item">
                <i className="fas fa-clock"></i>
                <span>{expiryDate.toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>
        
        <div className="card-actions">
          {isActive && (
            <button 
              className="btn-primary"
              onClick={() => navigate(`/play-session/${session.id}`)}
            >
              <i className="fas fa-play"></i>
              Join Session
            </button>
          )}
          <button 
            className="btn-delete"
            onClick={(e) => {
              e.stopPropagation();
              if (window.confirm('Are you sure you want to delete this session?')) {
                handleDeleteSession(session.id);
              }
            }}
            title="Delete session"
          >
            <i className="fas fa-trash-alt"></i>
            {!isActive && 'Delete'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="sessions-page">
      <NavBar />

      {user ? (
        <div className="sessions-container">
          {/* Hero Section */}
          <div className="hero-section">
            <div className="hero-content">
              <div className="hero-text">
                <h1>🎤 Your Karaoke Sessions</h1>
                <p>Create, manage, and join karaoke sessions with friends</p>
              </div>
              <div className="hero-stats">
                <div className="stat-card">
                  <i className="fas fa-music"></i>
                  <div>
                    <span className="stat-number">{activeSessions.length}</span>
                    <span className="stat-label">Active</span>
                  </div>
                </div>
                <div className="stat-card">
                  <i className="fas fa-history"></i>
                  <div>
                    <span className="stat-number">{inactiveSessions.length}</span>
                    <span className="stat-label">History</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Create Session Form */}
            <div className="hero-create-session">
              <h3>Create New Session</h3>
              <div className="create-session-form">
                <input
                  type="text"
                  value={newSession}
                  onChange={(e) => setNewSession(e.target.value)}
                  placeholder="Enter session name..."
                  disabled={sessionEligibility && !sessionEligibility.canCreateSession}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && newSession.trim() && (!sessionEligibility || sessionEligibility.canCreateSession)) {
                      handleCreateSession();
                    }
                  }}
                />
                <button 
                  onClick={handleCreateSession}
                  disabled={!newSession.trim() || (sessionEligibility && !sessionEligibility.canCreateSession)}
                  className="hero-create-btn"
                >
                  <i className="fas fa-plus"></i>
                  Create Session
                </button>
              </div>
              {sessionEligibility && sessionEligibility.canCreateSession && sessionEligibility.subscriptionPlan === 'free' && (
                <p className="trial-info">
                  <i className="fas fa-gift"></i> Free accounts can create up to 2 sessions. Upgrade for unlimited.
                </p>
              )}
              {sessionEligibility && !sessionEligibility.canCreateSession && (
                <div className="create-blocked-info">
                  <i className="fas fa-info-circle"></i>
                  <span>{sessionEligibility.message}</span>
                  {sessionEligibility.requiresUpgrade && (
                    <button className="upgrade-link-btn" onClick={() => navigate('/pricing')}>
                      Upgrade Now
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="tabs-container">
            <button 
              className={`tab ${activeTab === 'active' ? 'active' : ''}`}
              onClick={() => setActiveTab('active')}
            >
              <i className="fas fa-play-circle"></i>
              Active Sessions
              <span className="tab-count">{activeSessions.length}</span>
            </button>
            <button 
              className={`tab ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              <i className="fas fa-history"></i>
              Session History
              <span className="tab-count">{inactiveSessions.length}</span>
            </button>
          </div>

          {/* Sessions Grid */}
          <div className="sessions-grid-container">
            {activeTab === 'active' && (
              <div className="sessions-grid">
                {loading ? (
                  [0, 1, 2].map(i => renderSkeletonCard(i))
                ) : activeSessions.length > 0 ? (
                  activeSessions.map(session => renderSessionCard(session, true))
                ) : (
                  <div className="empty-state">
                    <div className="empty-icon">
                      <i className="fas fa-music"></i>
                    </div>
                    <h3>No Active Sessions</h3>
                    <p>Create a new karaoke session to get started!</p>
                    <button 
                      className="empty-state-btn"
                      onClick={() => document.querySelector('.hero-create-session input').focus()}
                    >
                      <i className="fas fa-plus"></i>
                      Create Session
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <div className="sessions-grid">
                {loading ? (
                  [0, 1, 2].map(i => renderSkeletonCard(`h${i}`))
                ) : inactiveSessions.length > 0 ? (
                  inactiveSessions.map(session => renderSessionCard(session, false))
                ) : (
                  <div className="empty-state">
                    <div className="empty-icon">
                      <i className="fas fa-history"></i>
                    </div>
                    <h3>No Session History</h3>
                    <p>Completed and expired sessions will appear here</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="login-prompt">
          <i className="fas fa-lock"></i>
          <h3>Sign In Required</h3>
          <p>Please log in to create and manage your karaoke sessions.</p>
        </div>
      )}
      <Footer />
    </div>
  );
};

export default SessionsPage;
